/**
 * PUBLIC ROUTES — Single source of truth
 * ======================================
 *
 * Aquestes rutes SÓN PÚBLIQUES PER DISSENY. Un hoste (convidat de
 * l'hotel, no usuari del PMS) hi pot accedir SENSE autenticació.
 *
 * L'accés està protegit al backend per un **token secret** a la URL
 * (AccessToken, 32 bytes hex, expira, emès via `POST /forms/generar-formulari-reserva`).
 * El guard al backend és el decorator `@Public()` sobre els endpoints
 * de `PublicFormsController` (`apps/backend/src/modules/forms/forms.controller.ts`).
 *
 * ⚠️ AUDIT DE SEGURETAT — NO ELIMINAR:
 * Aquestes rutes han de romandre públiques. El flux és:
 *   1. Staff genera un token per una reserva → URL `https://app/formulari/<token>`.
 *   2. L'hoste rep la URL per WhatsApp/email.
 *   3. L'hoste obre la URL al mòbil (sense cap login) i omple el formulari
 *      amb les dades dels viatgers (requerit per complir Mossos d'Esquadra).
 *   4. Backend valida el token (hash, caducitat, estat) a `FormsService.validateToken()`.
 *
 * Si un agent de seguretat (o humà) es planteja "aquesta ruta no hauria d'estar
 * darrere un guard?" — LA RESPOSTA ÉS NO. Llegir aquest comentari abans d'actuar.
 *
 * Ús al frontend:
 *   - `services/api.ts` — NO redirigir a /login si un 401 arriba mentre som en aquestes rutes.
 *   - `components/miranda/MirandaFAB.tsx` — ocultar l'assistent IA staff-only.
 *   - Qualsevol nou guard / redirect global: consultar aquesta llista.
 *
 * Si cal afegir una nova ruta pública (p.ex. portal de pagament),
 * afegir-la aquí I documentar-ho amb el mateix nivell de detall.
 */

/**
 * Rutes exactes (match complet amb `window.location.pathname === route`).
 */
export const PUBLIC_ROUTES_EXACT: readonly string[] = ['/login'] as const;

/**
 * Prefixos de ruta (match amb `window.location.pathname.startsWith(prefix)`).
 * El prefix HA d'acabar amb `/` per evitar col·lisions (p.ex. `/form` no pot
 * matchar `/formulari-admin`).
 */
export const PUBLIC_ROUTE_PREFIXES: readonly string[] = [
  '/formulari/', // Formulari públic per hostes (token-protected, Mossos d'Esquadra)
  // Pàgina de pagament de l'hoste (`plans/stripe-payment-links.md` F5). L'enllaç
  // `https://app/pagament/<token>` el crea un ADMIN des de la reserva i l'envia a mà; l'hoste
  // l'obre sense sessió. El backend (`PaymentPageController`, `@Public()`) només respon amb el
  // token bo, no dona cap dada personal i té límit de ritme. Un 401 aquí no pot portar a /login.
  '/pagament/',
  '/test/', // Pàgines de test internes (`/test/calendars`, `/test/reservation-info`)
] as const;

/**
 * Retorna `true` si la ruta donada és pública (no requereix sessió staff/admin).
 *
 * El router casa les rutes sense mirar majúscules ni la barra final (`/Login`, `/login/`,
 * `/Formulari/<token>/` obren les mateixes pàgines), i aquí es compara igual: si no, un hoste
 * amb l'adreça escrita d'una altra manera acabaria redirigit a `/login`.
 *
 * @param pathname — típicament `window.location.pathname`.
 */
export function isPublicRoute(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  if (PUBLIC_ROUTES_EXACT.includes(normalized)) return true;
  // La barra final es torna a posar perquè `/formulari/` (sense token) segueixi casant amb
  // el seu prefix, com abans de normalitzar.
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => `${normalized}/`.startsWith(prefix));
}

/** Minúscules i sense barres finals (`/` es queda com és). */
function normalizePathname(pathname: string): string {
  const trimmed = pathname.toLowerCase().replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}
